import { storeJWT, getCurrentUser } from "./auth";
import { installFetchInterceptor } from "./fetchInterceptor";

// sessionStorage key for deferring board loads to after Excalidraw init
const PENDING_BOARD_KEY = "syntrofi_pending_board";

export function applyLastBoardToHash(): void {
  installFetchInterceptor();

  // Pre-fill the Excalidraw collab username from GitHub login so cursors
  // show the real name instead of a random generated one.
  const user = getCurrentUser();
  if (user?.login) {
    localStorage.setItem("excalidraw-collab", JSON.stringify({ username: user.login }));
  }

  // For logged-in users we NEVER set a #json= hash before Excalidraw
  // initializes. Excalidraw's initializeScene shows a "replace your content?"
  // dialog whenever it sees a #json= hash AND local elements in localStorage.
  // Instead, we stash the board target in sessionStorage and load it
  // programmatically in onSceneInitialized after the scene is ready.
  const isLoggedIn = !!localStorage.getItem("syntrofi_jwt");

  if (isLoggedIn) {
    // OAuth callback: #token=<jwt> -> store JWT, then restore own board
    if (window.location.hash.startsWith("#token=")) {
      const token = window.location.hash.slice(7);
      storeJWT(token);
      window.history.replaceState({}, "", window.location.origin);
      const lastBoard = localStorage.getItem("syntrofi_board");
      if (lastBoard) {
        sessionStorage.setItem(PENDING_BOARD_KEY, lastBoard);
      }
      return;
    }

    // Any #json= hash (own board or share link): stash and clear.
    // This prevents the "replace content?" dialog entirely.
    const hashMatch = window.location.hash.match(
      /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
    );
    if (hashMatch) {
      sessionStorage.setItem(PENDING_BOARD_KEY, `${hashMatch[1]},${hashMatch[2]}`);
      window.history.replaceState({}, "", window.location.origin);
      return;
    }

    // No hash on startup: stash own board if one is saved
    const lastBoard = localStorage.getItem("syntrofi_board");
    if (lastBoard) {
      sessionStorage.setItem(PENDING_BOARD_KEY, lastBoard);
    }
    return;
  }

  // Anon user flow: use the hash directly so Excalidraw loads it normally.
  if (window.location.hash.startsWith("#token=")) {
    const token = window.location.hash.slice(7);
    storeJWT(token);
    window.history.replaceState({}, "", window.location.origin);
    const lastBoard = localStorage.getItem("syntrofi_board");
    if (lastBoard) {
      window.location.hash = `json=${lastBoard}`;
    }
    return;
  }

  if (!window.location.hash) {
    const lastBoard = localStorage.getItem("syntrofi_board");
    if (lastBoard) {
      window.location.hash = `json=${lastBoard}`;
    }
  }
}

export function consumePendingBoard(): { id: string; key: string } | null {
  const pending = sessionStorage.getItem(PENDING_BOARD_KEY);
  if (!pending) return null;
  sessionStorage.removeItem(PENDING_BOARD_KEY);
  const parts = pending.split(",");
  return parts.length === 2 && parts[0] && parts[1]
    ? { id: parts[0], key: parts[1] }
    : null;
}
